import {cn} from '@cohort/shared-frontend/utils/classNames';
import {isDark} from '@cohort/shared-frontend/utils/isDark';
import type {VariantProps} from 'class-variance-authority';
import {cva} from 'class-variance-authority';
import React from 'react';
import {useEffect} from 'react';

type BtnTextTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize';

export const previewButtonVariants = cva(
  'flex w-full items-center justify-center px-4 text-sm font-medium',
  {
    variants: {
      variant: {
        primary: cn(
          'hover:bg-[--xps-primary-btn-hover-background-color] hover:text-[--xps-primary-btn-hover-color]',
          'border-[--xps-primary-btn-border-color]',
          'text-[--xps-primary-btn-color] hover:text-[--xps-primary-btn-hover-color]'
        ),
        secondary: cn(
          'hover:bg-[--xps-secondary-btn-hover-background-color] hover:text-[--xps-secondary-btn-hover-color]',
          'border-[--xps-secondary-btn-border-color] bg-[--xps-secondary-btn-background-color]',
          'text-[--xps-secondary-btn-color] hover:text-[--xps-secondary-btn-hover-color]'
        ),
      },
      size: {
        default: 'h-11',
        small: 'h-8',
        large: 'h-12',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

type PreviewButtonProps = {
  text: string | JSX.Element;
  className?: string;
  onClick?: () => void;
  size?: 'default' | 'small' | 'large';
} & VariantProps<typeof previewButtonVariants> &
  (
    | {
        variant: 'primary';
        accentColorCode: string;
        backgroundColorCode?: string;
      }
    | {
        variant: 'secondary';
        backgroundColorCode: string;
        accentColorCode?: string;
      }
  );

const PreviewButton: React.FC<PreviewButtonProps> = ({
  backgroundColorCode,
  accentColorCode,
  text,
  className,
  variant,
  size = 'default',
  onClick,
}) => {
  useEffect(() => {
    const root = document.documentElement;
    if (variant === 'primary') {
      const hasDarkAccent = isDark(accentColorCode);
      root.style.setProperty('--xps-primary-btn-color', hasDarkAccent ? '#ffffff' : '#000000');
      root.style.setProperty(
        '--xps-primary-btn-hover-color',
        hasDarkAccent ? '#ffffff' : '#000000'
      );
    } else {
      const hasDarkBackground = isDark(backgroundColorCode);
      root.style.setProperty(
        '--xps-secondary-btn-color',
        hasDarkBackground ? '#ffffff' : '#000000'
      );
      root.style.setProperty(
        '--xps-secondary-btn-hover-color',
        hasDarkBackground ? '#ffffff' : '#000000'
      );
    }
  }, [backgroundColorCode, accentColorCode, variant]);

  return (
    <button
      onClick={onClick}
      className={cn(previewButtonVariants({variant, size}), className)}
      style={{
        backgroundColor: variant === 'primary' ? accentColorCode : undefined,
        borderRadius:
          variant === 'primary'
            ? 'var(--xps-primary-btn-border-radius)'
            : 'var(--xps-secondary-btn-border-radius)',
        textTransform: (variant === 'primary'
          ? 'var(--xps-primary-btn-text-transform)'
          : 'var(--xps-secondary-btn-text-transform)') as BtnTextTransform,
      }}
      type="button"
    >
      {text}
    </button>
  );
};

export default PreviewButton;
